// File: src/pages/StaffingDashboard.jsx

import React, { useEffect, useState } from 'react';
import StaffingSmartCard from '@/components/StaffingSmartCard';
import ShiftApprovalPanel from '@/components/Shifts/ShiftApprovalPanel';
import LeaveApprovalPanel from '@/components/Staff/LeaveApprovalPanel';
import StaffInsightsTable from '@/components/Staff/StaffInsightsTable';
import StaffingAlertsPanel from '@/components/Staff/StaffingAlertsPanel';
import { useNavigate } from 'react-router-dom';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';

const StaffingDashboard = () => {
  const [isLocked, setIsLocked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkLock = async () => {
      const status = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(status, ['cyberattack', 'offline']));
    };
    checkLock();
  }, []);

  return (
    <div className="p-6 bg-background min-h-screen text-white space-y-6">
      <h1 className="text-3xl font-bold">🧑‍💼 Staffing Dashboard</h1>

      {/* Smart Summary */}
      <StaffingSmartCard />

      {/* Emergency Banner */}
      {isLocked && (
        <div className="bg-red-600 p-4 rounded-xl font-semibold text-center">
          🚨 Emergency Lock Active — Staffing Actions Temporarily Disabled
        </div>
      )}

      {/* Alerts and Approvals */}
      {!isLocked && (
        <>
          <StaffingAlertsPanel />
          <ShiftApprovalPanel />
          <LeaveApprovalPanel />
        </>
      )}

      {/* Insights Table */}
      {!isLocked && <StaffInsightsTable />}

      {/* Navigation Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        <button
          onClick={() => navigate('/staffing/rota-grid')}
          className="bg-blue-600 hover:bg-blue-700 px-4 py-3 rounded-xl text-left shadow"
        >
          📅 <strong>Plan Rota</strong>
          <p className="text-sm text-white/80">View and assign shifts</p>
        </button>
        <button
          onClick={() => navigate('/payroll-report')}
          className="bg-green-600 hover:bg-green-700 px-4 py-3 rounded-xl text-left shadow"
        >
          💰 <strong>View Payroll</strong>
          <p className="text-sm text-white/80">Review wage costs & payouts</p>
        </button>
        <button
          onClick={() => navigate('/staff-directory')}
          className="bg-gray-700 hover:bg-gray-800 px-4 py-3 rounded-xl text-left shadow"
        >
          👥 <strong>Staff Directory</strong>
          <p className="text-sm text-white/80">Manage team & pay rates</p>
        </button>
        <button
          onClick={() => navigate('/agency-shifts')}
          className="bg-yellow-700 hover:bg-yellow-800 px-4 py-3 rounded-xl text-left shadow"
        >
          🧑‍💼 <strong>Agency Shifts</strong>
          <p className="text-sm text-white/80">Manage agency bookings</p>
        </button>
        <button
          onClick={() => navigate('/clock-in')}
          className="bg-purple-700 hover:bg-purple-800 px-4 py-3 rounded-xl text-left shadow"
        >
          ⏱ <strong>QR Clock-In</strong>
          <p className="text-sm text-white/80">Mobile time tracking</p>
        </button>
        <button
          onClick={() => navigate('/staffing/payroll-dashboard')}
          className="bg-teal-600 hover:bg-teal-700 px-4 py-3 rounded-xl text-left shadow"
        >
          📊 <strong>Smart Payroll Analytics</strong>
          <p className="text-sm text-white/80">Explore staffing costs & trends</p>
        </button>
      </div>
    </div>
  );
};

export default StaffingDashboard;
